import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BriefcaseBusinessIcon, CalendarClockIcon, CalendarDaysIcon, FileBadgeIcon, FileTextIcon, GraduationCapIcon, HouseIcon, MapPinIcon, ShieldAlertIcon, StoreIcon, TriangleAlertIcon, UserIcon, WalletIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Meter } from '../../components/ui/Meter';
import { WelcomeHero, type HeroStat } from '../../components/dashboard/WelcomeHero';
import { QuickAccessCard, type QuickAccessItem } from '../../components/dashboard/QuickAccessCard';
import { PremiumBanner } from '../../components/dashboard/PremiumBanner';
import { useAccount } from '../../contexts/AccountContext';
import { useDataMode } from '../../contexts/DataModeContext';
import { FirstRun } from '../../components/platform/FirstRun';
import { dashboardImages } from '../../data/dashboardVisual';
import { assignments, attendance, cgpa, timetable } from '../../data/academics';
import { myApplications } from '../../data/housing';
import { rentLedger } from '../../data/property';
import { events } from '../../data/campus';
import { jobApplications } from '../../data/career';
import { alerts } from '../../data/support';
import { naira, percent } from '../../utils/format';
const TODAY = 'Thu';

/**
 * Six actions, chosen because they are the ones a student starts from rather
 * than arrives at. Each is a real destination — nothing here is a placeholder.
 */
const QUICK_ACTIONS: QuickAccessItem[] = [{
  to: '/academics',
  label: 'Academics',
  detail: 'Register, timetable, results',
  icon: GraduationCapIcon,
  tone: 'bg-brand-50 text-brand-700'
}, {
  to: '/money',
  label: 'Money',
  detail: 'Fees, bills, records',
  icon: WalletIcon,
  tone: 'bg-info-50 text-info-700'
}, {
  to: '/housing',
  label: 'Housing',
  detail: 'Search and applications',
  icon: HouseIcon,
  tone: 'bg-ok-50 text-ok-700'
}, {
  to: '/market',
  label: 'Marketplace',
  detail: 'Food, books, services, gigs',
  icon: StoreIcon,
  tone: 'bg-warn-50 text-warn-700'
}, {
  to: '/services',
  label: 'Campus services',
  detail: 'Transcripts, references',
  icon: FileBadgeIcon,
  tone: 'bg-[#f2e9fa] text-[#7e22ce]'
}, {
  to: '/opportunities',
  label: 'Opportunities',
  detail: 'Jobs and internships',
  icon: BriefcaseBusinessIcon,
  tone: 'bg-[#e7eafc] text-[#3730a3]'
}];
export function Home() {
  const {
    account,
    institutionName,
    campusName
  } = useAccount();
  const {
    isNew
  } = useDataMode();
  if (isNew) {
    return <FirstRun surface="Home" what="This is where Campuzon answers what you need to do today." why="Once your academic identity is verified and you have started applying, registering, or looking for somewhere to live, this page leads with whatever is closest to a deadline — and stays quiet when nothing is." note="Campuzon does not invent your timetable or your results. Those appear once your institution publishes them or you enter them yourself, and each one says which of the two it is." steps={[{
      label: 'Verify your academic identity',
      detail: 'It decides what you can prove to landlords, employers and communities. Nothing else here works as well without it.',
      to: '/identity'
    }, {
      label: 'Finish setting up your account',
      detail: 'Campus, programme, photo and what you want to hear about.',
      to: '/settings'
    }, {
      label: 'Find your people',
      detail: 'Join a course or department community so your campus feed has something in it.',
      to: '/social'
    }]} data-id="element-10158" data-mp="37500yy4einaw" />;
  }
  const todaysClasses = timetable.filter(session => session.day === TODAY);
  const openWork = assignments.filter(item => item.status !== 'graded' && item.status !== 'submitted').slice(0, 4);
  const atRisk = attendance.map(record => ({
    ...record,
    rate: percent(record.attended, record.held)
  })).filter(record => record.rate < record.requirement);
  const application = myApplications[0];
  const rentDue = rentLedger.find(record => record.tenant === account.fullName);
  const criticalAlert = alerts.find(alert => alert.severity === 'critical');
  const nextEvent = events.find(event => event.registeredByMe);
  const activeApplications = jobApplications.filter(item => item.stage !== 'Closed');
  const overdue = openWork.filter(item => item.status === 'overdue');
  const firstName = account.fullName.split(' ')[0];
  const stats: HeroStat[] = [{
    id: 'classes',
    value: todaysClasses.length,
    label: 'Classes today',
    to: '/academics'
  }, {
    id: 'tasks',
    value: openWork.length,
    label: 'Open tasks',
    to: '/academics'
  }, {
    id: 'overdue',
    value: overdue.length,
    label: 'Overdue',
    to: '/academics'
  }, {
    id: 'announcements',
    value: events.length,
    label: 'Announcements',
    to: '/social'
  }];
  return <div className="space-y-8" data-id="element-10159" data-mp="2l51acgloifo8">
      <WelcomeHero greeting="Good afternoon" firstName={firstName} programme={account.academic.programme} level={account.academic.level} institutionName={institutionName} campusName={campusName} status={account.academic.status} stats={stats} data-id="element-10160" data-mp="2l6vp1d6cxbjv" />

      {criticalAlert && <div className="flex flex-col gap-3 rounded-card border border-danger-100 bg-danger-50 px-5 py-4 sm:flex-row sm:items-center" data-id="element-10161" data-mp="22d042t6krn4s">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white" data-id="element-10162" data-mp="1xf6xhz1d0r3z">
            <ShieldAlertIcon aria-hidden className="h-5 w-5 text-danger-700" data-id="element-10163" data-mp="1shygw2vv0v4u" />
          </span>
          <div className="min-w-0 flex-1" data-id="element-10164" data-mp="3rs6iy8sf8fj6">
            <p className="text-body font-semibold text-ink" data-id="element-10165" data-mp="3mtd86o4ir2mp">{criticalAlert.title}</p>
            <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10166" data-mp="1l4pdx7jp930p">{criticalAlert.detail}</p>
          </div>
          <Link to="/support" className="group inline-flex min-h-touch shrink-0 items-center gap-1.5 text-body-sm font-semibold text-danger-700 hover:text-danger-800" data-id="element-10167" data-mp="1pv5u2zewn2qr">
            Campus safety
            <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10168" data-mp="1g6nz4ytb3pv1" />
          </Link>
        </div>}

      <section aria-labelledby="quick-access" data-id="element-10169" data-mp="28syxs3xcrgp7">
        <h2 id="quick-access" className="text-h4 font-semibold text-ink" data-id="element-10170" data-mp="1uuiruf7bdu84">
          Quick access
        </h2>
        <ul className="mt-3.5 grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-6" data-id="element-10171" data-mp="1yvu8vwc4h74p">
          {QUICK_ACTIONS.map(action => <li key={action.to} data-id="element-10172" data-mp="jeak7zd0zspl">
              <QuickAccessCard item={action} data-id="element-10173" data-mp="jhgb9k57t4ny" />
            </li>)}
        </ul>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-10174" data-mp="neujb8h5t6ed">
        <div className="space-y-6" data-id="element-10175" data-mp="fvuzx2tp7ev0">
          <Panel title="Today — Thursday, 13 August" description="Your timetable and the work closest to its deadline." action={<Link to="/academics" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 transition-colors duration-150 ease-out hover:text-brand-800" data-id="element-10177" data-mp="puu9cnmafej2">
                View timetable
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10178" data-mp="3oekc0zcen2xc" />
              </Link>} bodyClassName="p-0" data-id="element-10176" data-mp="aycz5zpu45y3">
            {todaysClasses.length === 0 ? <div className="px-5 py-8 text-center" data-id="element-10179" data-mp="3ak9xfau4gp50">
                <CalendarDaysIcon aria-hidden className="mx-auto h-8 w-8 text-ink-subtle" data-id="element-10180" data-mp="35n9q6wljdgcb" />
                <p className="mt-3 text-body font-semibold text-ink" data-id="element-10181" data-mp="2k3oqnv4c7ppr">No classes today</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10182" data-mp="2p2i79wex7wu5">
                  Nothing is scheduled on your timetable for today.
                </p>
              </div> : <ol className="divide-y divide-line" data-id="element-10183" data-mp="2u3z3hemgmv4e">
                {todaysClasses.map(session => <li key={session.id} className="flex gap-4 px-5 py-4" data-id="element-10184" data-mp="332asxdblbmv9">
                    {/* Time block reads as a timeline rather than a table cell. */}
                    <div className="w-16 shrink-0 border-l-2 border-brand-200 pl-3" data-id="element-10185" data-mp="37zt294tis43s">
                      <span className="cz-num block text-body font-semibold text-ink" data-id="element-10186" data-mp="3ctf6c8lmaznz">
                        {session.start}
                      </span>
                      <span className="cz-num mt-0.5 block text-caption text-ink-subtle" data-id="element-10187" data-mp="3lsskhekoscf1">
                        {session.end}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-10188" data-mp="3qpiwnqtdenv6">
                      <p className="text-body font-semibold text-ink" data-id="element-10189" data-mp="1f5l1xqxswoxi">{session.title}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-body-sm text-ink-muted" data-id="element-10190" data-mp="1a55qj4x2xri6">
                        <span className="rounded-md bg-canvas px-2 py-0.5 text-caption font-medium text-ink" data-id="element-10191" data-mp="164mwr5bmmbdc">
                          {session.courseCode}
                        </span>
                        <span className="inline-flex items-center gap-1" data-id="element-10192" data-mp="1xtyfghfaaz3a">
                          <MapPinIcon aria-hidden className="h-4 w-4" data-id="element-10193" data-mp="1ttoczeqpaqt6" />
                          {session.venue}
                        </span>
                        <span data-id="element-10194" data-mp="1p14x2b4jjjl7">{session.mode}</span>
                      </p>
                    </div>
                    <CalendarDaysIcon aria-hidden className="mt-1 hidden h-5 w-5 shrink-0 text-ink-subtle sm:block" data-id="element-10195" data-mp="1k33dzq747k9q" />
                  </li>)}
              </ol>}

            <div className="border-t border-line bg-canvas px-5 py-4" data-id="element-10196" data-mp="12bauiz31idhp">
              <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10197" data-mp="12ddqdzqvj0uc">
                Due next
              </p>
              <ul className="mt-3 space-y-3" data-id="element-10198" data-mp="1bchan71s1maf">
                {openWork.map(item => <li key={item.id} className="flex items-start justify-between gap-4" data-id="element-10199" data-mp="17a44win15odn">
                    <div className="min-w-0" data-id="element-10200" data-mp="1la58i5uoj9wt">
                      <p className="truncate text-body-sm font-semibold text-ink" data-id="element-10201" data-mp="1l8u1zy9uno20">{item.title}</p>
                      <p className="text-caption text-ink-muted" data-id="element-10202" data-mp="1gask3go5tus4">
                        {item.courseCode} · {item.weight}% of course mark
                      </p>
                    </div>
                    <Badge tone={item.status === 'overdue' ? 'danger' : item.dueLabel.startsWith('Tomorrow') ? 'warn' : 'neutral'} data-id="element-10203" data-mp="1q2g4ixmaubk3">
                      {item.dueLabel}
                    </Badge>
                  </li>)}
              </ul>
              <Link to="/academics" className="group mt-4 inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10204" data-mp="29n6yegjfgah9">
                View all tasks
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10205" data-mp="1zwc20jcgvui3" />
              </Link>
            </div>
          </Panel>

          {atRisk.length > 0 && <Panel title="Attendance needs attention" description="Courses currently below the 75% requirement." action={<Link to="/academics" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10207" data-mp="1lxckrwraahvy">
                  View details
                  <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10208" data-mp="3iv06tbxjad05" />
                </Link>} data-id="element-10206" data-mp="1ux97hsu456od">
              <ul className="space-y-5" data-id="element-10209" data-mp="afzxfz62pt1r">
                {atRisk.map(record => <li key={record.courseCode} data-id="element-10210" data-mp="advt5g49is0y">
                    <div className="flex items-center gap-2" data-id="element-10211" data-mp="5gn9fb3gufxl">
                      <TriangleAlertIcon aria-hidden className="h-4 w-4 text-warn-700" data-id="element-10212" data-mp="1fa6b8rrcqud" />
                      <p className="text-body font-semibold text-ink" data-id="element-10213" data-mp="1h4r8n314iyg">{record.courseCode}</p>
                      <span className="cz-num ml-auto text-body font-semibold text-ink" data-id="element-10214" data-mp="3snpd7se890om">
                        {record.rate}%
                      </span>
                    </div>
                    <div className="mt-2" data-id="element-10215" data-mp="2h4v49unn5fwb">
                      <Meter value={record.rate} tone="warn" height="thin" data-id="element-10216" data-mp="wonk3bla0tbh" />
                    </div>
                    <p className="cz-num mt-1.5 text-caption text-ink-muted" data-id="element-10217" data-mp="11n8hz4d230wr">
                      {record.attended} of {record.held} classes attended · {record.requirement}%
                      required to sit the exam
                    </p>
                  </li>)}
              </ul>
            </Panel>}

          <Panel title="Campus updates" description="What your campus and communities published recently." action={<Link to="/social" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10219" data-mp="1pdcaogophg8z">
                View all
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10220" data-mp="3r5h98b0pnbso" />
              </Link>} bodyClassName="p-0" data-id="element-10218" data-mp="1fe3ls28p6rth">
            <ul className="divide-y divide-line" data-id="element-10221" data-mp="2vu9azn34c31o">
              {events.slice(0, 3).map(event => <li key={event.id} className="flex items-start gap-4 px-5 py-4 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-10222" data-mp="39tsajrhv662d">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50" data-id="element-10223" data-mp="2h5cxhaialxto">
                    <CalendarClockIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-10224" data-mp="2c6s5tzh3wmgu" />
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-10225" data-mp="2qvfx3tjx6a0z">
                    <p className="text-body font-semibold text-ink" data-id="element-10226" data-mp="2qwzg3rwymz6g">{event.title}</p>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10227" data-mp="2m350azkh74cn">
                      {event.dateLabel}, {event.time} · {event.venue}
                    </p>
                  </div>
                  {event.registeredByMe && <Badge tone="ok" data-id="element-10228" data-mp="1tdo7i0sy1lfw">Registered</Badge>}
                </li>)}
            </ul>
          </Panel>
        </div>

        <div className="space-y-6" data-id="element-10229" data-mp="dozxhqeelh59">
          {/* Image-led card: a property is a place, so it leads with a photograph. */}
          <Panel title="Housing" action={<Link to="/housing" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10231" data-mp="3y57cay1dxej">
                View all
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10232" data-mp="wm14thvfjtcd" />
              </Link>} bodyClassName="p-0" data-id="element-10230" data-mp="3w3qwaqfkhpwv">
            <div className="flex gap-4 border-b border-line px-5 py-4" data-id="element-10233" data-mp="33d8wpze1y6o7">
              <div className="min-w-0 flex-1" data-id="element-10234" data-mp="33g4spi6cala0">
                <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10235" data-mp="38dwju9z5u3d5">
                  Application
                </p>
                <p className="mt-1.5 text-body font-semibold text-ink" data-id="element-10236" data-mp="1dyhckc09e7s0">{application.listingName}</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10237" data-mp="18zwdwh0nnw5o">
                  Submitted {application.submitted} · move-in {application.moveIn}
                </p>
                <Badge tone="brand" className="mt-2.5" data-id="element-10238" data-mp="104qdxyo2g8b2">
                  In review by the operator
                </Badge>
              </div>
              <img src={dashboardImages.housing} alt={`${application.listingName} building exterior`} loading="lazy" className="h-20 w-20 shrink-0 rounded-xl object-cover" data-id="element-10239" data-mp="q72amkce9sha" />
            </div>
            {rentDue && <div className="border-b border-line px-5 py-4" data-id="element-10240" data-mp="2fm08pwyzyvf7">
                <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-10241" data-mp="2fl7d9s5hnscd">
                  Rent
                </p>
                <p className="cz-num mt-1.5 text-h3 font-bold text-ink" data-id="element-10242" data-mp="3ti9z20hq5wbd">{naira(rentDue.amount)}</p>
                <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10243" data-mp="2bh2j9xvmld6">
                  {rentDue.period} for {rentDue.unit} · due {rentDue.due}
                </p>
                <p className="mt-2 text-caption text-ink-subtle" data-id="element-10244" data-mp="738sbi97kn29">
                  Payments are handled by HavaPay. Campuzon keeps the record only.
                </p>
              </div>}
            <div className="px-5 py-4" data-id="element-10245" data-mp="3al0f8i8hmgtl">
              <Link to="/housing" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10246" data-mp="3fhzbp7fmizuy">
                Continue your housing search
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10247" data-mp="3ok8hxl3v072j" />
              </Link>
            </div>
          </Panel>

          {nextEvent && <Panel title="You are registered for" action={<Link to="/community" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10249" data-mp="ahuj2nlvv5d8">
                  View calendar
                  <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10250" data-mp="jfmzjv3ccj27" />
                </Link>} bodyClassName="p-0" data-id="element-10248" data-mp="fh7ehwqtlkrf">
              <div className="relative overflow-hidden px-5 py-4" data-id="element-10251" data-mp="176sfea9a7bci">
                <img src={dashboardImages.campus} alt="" loading="lazy" className="pointer-events-none absolute -bottom-2 right-0 h-24 w-40 object-contain opacity-80" data-id="element-10252" data-mp="135gxkqwnfuev" />
                <div className="relative flex gap-3 pr-24" data-id="element-10253" data-mp="34zg8hb5v09r1">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50" data-id="element-10254" data-mp="2uug8cuf9bs61">
                    <CalendarClockIcon aria-hidden className="h-4 w-4 text-brand-700" data-id="element-10255" data-mp="2ytyshnz97z7t" />
                  </span>
                  <div className="min-w-0" data-id="element-10256" data-mp="2l193rbsccg70">
                    <p className="text-body font-semibold text-ink" data-id="element-10257" data-mp="2q1n2u4bysfsw">{nextEvent.title}</p>
                    <p className="mt-1 text-body-sm text-ink-muted" data-id="element-10258" data-mp="2b4nziso5b948">
                      {nextEvent.dateLabel}, {nextEvent.time}
                    </p>
                    <p className="text-body-sm text-ink-muted" data-id="element-10259" data-mp="2g2609vs0ei15">{nextEvent.venue}</p>
                  </div>
                </div>
              </div>
            </Panel>}

          {/* Number-first card: the figure is the content, the label supports it. */}
          <Panel title="Your activity" bodyClassName="p-0" data-id="element-10260" data-mp="e60ralk9w2c">
            <dl className="divide-y divide-line" data-id="element-10261" data-mp="5c7ldzymy3fx">
              {[{
              label: 'CGPA',
              value: `${cgpa.toFixed(2)} / 5.00`,
              to: '/academics'
            }, {
              label: 'Registered units',
              value: '11',
              to: '/academics'
            }, {
              label: 'Live job applications',
              value: String(activeApplications.length),
              to: '/opportunities'
            }, {
              label: 'Open feedback threads',
              value: '2',
              to: '/support'
            }].map(row => <div key={row.label} className="flex items-baseline justify-between px-5 py-3.5" data-id="element-10262" data-mp="1rnwu4iezyhhm">
                  <dt className="text-body-sm text-ink-muted" data-id="element-10263" data-mp="1wkx0kufrdm99">
                    <Link to={row.to} className="hover:text-ink hover:underline" data-id="element-10264" data-mp="1im6z72lwp0f3">
                      {row.label}
                    </Link>
                  </dt>
                  <dd className="cz-num text-body font-bold text-ink" data-id="element-10265" data-mp="1mlz3h349oken">{row.value}</dd>
                </div>)}
            </dl>
            <div className="border-t border-line px-5 py-3.5" data-id="element-10266" data-mp="b8dg9nhywsvr">
              <Link to="/profile" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-10267" data-mp="b6irtz7hmimx">
                <UserIcon aria-hidden className="h-4 w-4" data-id="element-10268" data-mp="3uxrqo5s209c5" />
                View full activity
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-10269" data-mp="dl7f9huwpp23" />
              </Link>
            </div>
          </Panel>

          <div className="rounded-card border border-info-100 bg-info-50 px-5 py-4" data-id="element-10270" data-mp="3c5fnxhxhn61h">
            <p className="flex items-start gap-3 text-caption leading-5 text-ink-muted" data-id="element-10271" data-mp="3qtk1k7hoorde">
              <FileTextIcon aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-info-700" data-id="element-10272" data-mp="3m33ncl61h1pz" />
              <span data-id="element-10273" data-mp="2ydj21vmexsdj">
                <span className="block text-body-sm font-semibold text-ink" data-id="element-10274" data-mp="2yczhs89airwc">Explain Campuzon</span>
                {institutionName} shares its course directory with Campuzon but not student records,
                so your grades and attendance are the ones you enter yourself.
              </span>
            </p>
          </div>
        </div>
      </div>

      <PremiumBanner data-id="element-10275" data-mp="3n2c61k5hfwrk" />
    </div>;
}